import { BaseNode, type NodeConfig } from 'mflows';

// 定时触发器节点配置类型
interface TimerTriggerNodeConfig extends NodeConfig {
  triggerMode?: 'delay' | 'periodic';
  interval?: number;
  enabled?: boolean;
}

// 定时触发器数据类型
interface TimerTriggerData {
  timestamp: number;
  type: 'timer_trigger';
  triggerMode: 'delay' | 'periodic';
  count: number;
  value?: unknown;
}

// 初始节点配置类型
interface InitialNodeConfig extends NodeConfig {
  initialData?: any;
  autoTrigger?: boolean;
}

// 初始节点数据类型
interface InitialNodeData {
  timestamp: number;
  type: 'initial';
  value?: unknown;
}

// 定时触发器节点类
export class TimerTriggerNode extends BaseNode<TimerTriggerNodeConfig, TimerTriggerData> {
  static NodeType = '定时触发器';
  private timerId: number | null = null;
  private externalData: unknown = null;
  private triggerCount: number = 0;

  protected async initialize(): Promise<void> {
    // 定时触发器不需要任何输入口
    this.setInputPorts([]);
    this.setOutputPorts([
      { id: 'timer-data', dataType: 'any', name: '数据' },
      { id: 'timer-exec', dataType: 'flow', name: '执行' }
    ]);
    
    if (!this.settings.interval) {
      this.settings.interval = 1000;
    }
    
    if (this.settings.triggerMode === undefined) {
      this.settings.triggerMode = 'periodic';
    }
    
    if (this.settings.enabled === undefined) {
      this.settings.enabled = false;
    }
    
    if (this.settings.enabled) {
      this.startTimer();
    }
  }

  protected receiveData(pointId: string, data: unknown): void {
    // 定时触发器不消费上游数据，仅为了满足基类抽象方法要求
    void pointId; void data;
  }

  protected checkConfig(): boolean {
    return typeof this.settings.interval === 'number' && this.settings.interval > 0;
  }

  protected async processConfigUpdate(changes: Partial<TimerTriggerNodeConfig>): Promise<TimerTriggerData> {
    this.stopTimer();
    
    if (changes.interval !== undefined) {
      this.settings.interval = changes.interval;
    }
    
    if (changes.triggerMode !== undefined) {
      this.settings.triggerMode = changes.triggerMode;
    }
    
    if (changes.enabled !== undefined) {
      this.settings.enabled = changes.enabled;
    }
    
    if (this.settings.enabled) {
      this.startTimer();
    }
    
    const mode = this.settings.triggerMode ?? 'periodic';
    return {
      timestamp: Date.now(),
      type: 'timer_trigger',
      triggerMode: mode,
      count: this.triggerCount,
      value: this.externalData
    };
  }

  public startTimer(): void {
    this.stopTimer();
    
    const interval = this.settings.interval ?? 1000;
    const mode = this.settings.triggerMode ?? 'periodic';

    if (mode === 'delay') {
      this.triggerCount = 0;
      this.timerId = setTimeout(() => {
        this.emitTriggerEvent();
        this.timerId = null;
      }, interval) as unknown as number;
    } else {
      this.timerId = setInterval(() => {
        this.emitTriggerEvent();
        this.triggerCount++;
      }, interval) as unknown as number;
    }
  }

  public stopTimer(): void {
    if (this.timerId !== null) {
      const mode = this.settings.triggerMode ?? 'periodic';
      if (mode === 'delay') {
        clearTimeout(this.timerId);
      } else {
        clearInterval(this.timerId);
      }
      this.timerId = null;
    }
  }

  private emitTriggerEvent(): void {
    const mode = this.settings.triggerMode ?? 'periodic';
    const data: TimerTriggerData = {
      timestamp: Date.now(),
      type: 'timer_trigger',
      triggerMode: mode,
      count: this.triggerCount,
      value: this.externalData
    };
    
    this.setDataChange(data);
  }
  
  public destroy(): void {
    this.stopTimer();
    super.destroy();
  }
}

// 初始节点类
export class InitialNode extends BaseNode<InitialNodeConfig, InitialNodeData> {
  static NodeType = '初始节点';

  protected async initialize(): Promise<void> {
    this.setInputPorts([
      { id: 'initial-config', dataType: 'object', allowTypes: ['object'], name: '配置' }
    ]);
    this.setOutputPorts([
      { id: 'initial-data', dataType: 'any', name: '数据' },
      { id: 'initial-exec', dataType: 'flow', name: '执行' }
    ]);
    
    if (this.settings.autoTrigger === undefined) {
      this.settings.autoTrigger = true;
    }
    
    if (this.settings.autoTrigger) {
      this.emitInitialEvent();
    }
  }

  protected receiveData(pointId: string, data: unknown): void {
    void pointId; void data;
    // 初始节点不消费上游数据
  }

  protected checkConfig(): boolean {
    return true;
  }

  protected async processConfigUpdate(changes: Partial<InitialNodeConfig>): Promise<InitialNodeData> {
    if (changes.initialData !== undefined) {
      this.settings.initialData = changes.initialData;
    }
    
    if (changes.autoTrigger !== undefined) {
      this.settings.autoTrigger = changes.autoTrigger;
    }
    
    return {
      timestamp: Date.now(),
      type: 'initial',
      value: this.settings.initialData
    };
  }

  public trigger(): void {
    this.emitInitialEvent();
  }

  private emitInitialEvent(): void {
    const data: InitialNodeData = {
      timestamp: Date.now(),
      type: 'initial',
      value: this.settings.initialData || null
    };
    
    this.setDataChange(data);
  }
}